import { useAtomValue } from 'jotai/index';
import * as React from 'react';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    BodyLargeEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Icon,
    KeyboardCompatibleBottomSheet,
    Separator,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../common/utils/bankingUtils';
import { getCadenceDisplayString } from '../../../common/utils/billUtils';
import type { AccountSummary } from '../../../holistic/types/HolisticSummary';
import { billsAccountsAtom } from '../../state/BillsAtoms';
import {
    type _Bill,
    AiToPostgresBillingCadence,
    EditProposedChangesRequestAction,
    type PartialBill,
} from '../../types/Bill';

const SheetHeader: Record<EditProposedChangesRequestAction, string> = {
    [EditProposedChangesRequestAction.APPROVE]: 'Looks correct',
    [EditProposedChangesRequestAction.REJECT]: 'Looks wrong',
};

const SheetTitle: Record<EditProposedChangesRequestAction, string> = {
    [EditProposedChangesRequestAction.APPROVE]:
        "If correct, we'll update the bill to reflect the new changes",
    [EditProposedChangesRequestAction.REJECT]:
        'If you disregard these changes, this transaction should not impact the details of this bill',
};

const ButtonLabel: Record<EditProposedChangesRequestAction, string> = {
    [EditProposedChangesRequestAction.APPROVE]: 'Apply change',
    [EditProposedChangesRequestAction.REJECT]: 'Reject change',
};

interface ProposedChangesAttributesRowProps {
    action: EditProposedChangesRequestAction;
    attribute: string;
    oldValue: string;
    newValue: string;
}

const ProposedChangesAttributesRow = ({
    action,
    attribute,
    oldValue,
    newValue,
}: ProposedChangesAttributesRowProps) => {
    return (
        <XStack
            alignItems="center"
            justifyContent="space-between"
            paddingVertical={8}
            gap={8}
        >
            <YStack gap={4} flex={1}>
                <BodySmallRegular color="$gray1000">{`Current ${attribute}`}</BodySmallRegular>
                <BodyLargeEmphasized>{oldValue}</BodyLargeEmphasized>
            </YStack>
            <View
                alignItems="center"
                justifyContent="center"
                padding={8}
                borderRadius={50}
                borderWidth={1}
                borderColor="$gray200"
            >
                <Icon
                    icon={
                        action === EditProposedChangesRequestAction.APPROVE
                            ? ArrowRightIcon
                            : ArrowLeftIcon
                    }
                />
            </View>
            <YStack gap={4} flex={1}>
                <BodySmallRegular
                    textAlign="right"
                    color="$gray1000"
                >{`New ${attribute}`}</BodySmallRegular>
                <BodyLargeEmphasized textAlign="right" color="$orange1000">
                    {newValue}
                </BodyLargeEmphasized>
            </YStack>
        </XStack>
    );
};

interface ProposedChangesAttributesProps {
    bill: Required<_Bill> | Partial<_Bill>;
    action: EditProposedChangesRequestAction;
}

const ProposedChangesAttributes = ({
    bill,
    action,
}: ProposedChangesAttributesProps) => {
    const billAccounts = useAtomValue(billsAccountsAtom);

    const {
        accountId: oldAccountId,
        amount: oldAmount,
        cadence: oldFrequency,
    } = bill;
    const {
        accountId: newAccountId,
        averageAmount: newAmount,
        frequency: newFrequency,
    } = bill.proposedChanges ?? {};

    const oldAccount =
        oldAccountId != null
            ? (billAccounts?.find(
                  (a) => a.id === oldAccountId,
              ) as AccountSummary)
            : null;
    const newAccount =
        newAccountId != null
            ? (billAccounts?.find(
                  (a) => a.id === newAccountId,
              ) as AccountSummary)
            : null;

    return (
        <YStack gap={6} separator={<Separator />}>
            {newAmount != null && (
                <ProposedChangesAttributesRow
                    action={action}
                    attribute="amount"
                    oldValue={formatDollars(oldAmount)}
                    newValue={formatDollars(newAmount)}
                />
            )}
            {newFrequency != null && (
                <ProposedChangesAttributesRow
                    action={action}
                    attribute="frequency"
                    oldValue={getCadenceDisplayString(oldFrequency)}
                    newValue={getCadenceDisplayString(
                        AiToPostgresBillingCadence[newFrequency],
                    )}
                />
            )}
            {oldAccount && newAccount && (
                <ProposedChangesAttributesRow
                    action={action}
                    attribute="account"
                    oldValue={`${oldAccount.name} *${oldAccount.accountNumberMasked}`}
                    newValue={`${newAccount.name} *${newAccount.accountNumberMasked}`}
                />
            )}
        </YStack>
    );
};

interface BillDetailsProposedChangesSheetProps {
    bill: _Bill | PartialBill;
    action: EditProposedChangesRequestAction;
    onSubmit: (action: EditProposedChangesRequestAction) => void;
    onDismiss: () => void;
}

const BillDetailsProposedChangesSheet = ({
    bill,
    action,
    onSubmit,
    onDismiss,
}: BillDetailsProposedChangesSheetProps) => {
    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onDismiss}
            isInsideModal
            title={SheetHeader[action]}
            showCloseButton
        >
            <YStack gap={20}>
                <BodyLargeEmphasized>{SheetTitle[action]}</BodyLargeEmphasized>
                <ProposedChangesAttributes bill={bill} action={action} />
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    text={ButtonLabel[action]}
                    onPress={() => {
                        onSubmit(action);
                    }}
                />
            </YStack>
        </KeyboardCompatibleBottomSheet>
    );
};

export default BillDetailsProposedChangesSheet;
